import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Link2, X } from "lucide-react";

// Saved file-name → slip-name links for the Packing List qty comparison.
export const useSlipNameLinks = (entryId, onDiscs) => {
  const [slipNames, setSlipNames] = useState([]);
  const [aliases, setAliases] = useState([]);

  const loadAliases = useCallback(async () => {
    const r = await axios.get(`${API}/packing-list/name-aliases`, { headers: getAuthHeader() });
    setAliases(r.data || []);
  }, []);

  useEffect(() => {
    if (!entryId) return;
    axios.get(`${API}/packing-list/history/${entryId}/slip-products`, { headers: getAuthHeader() })
      .then(r => setSlipNames(r.data || [])).catch(() => setSlipNames([]));
    loadAliases().catch(() => {});
  }, [entryId, loadAliases]);

  const link = async (fileName, slipName) => {
    try {
      const r = await axios.post(`${API}/packing-list/name-aliases`,
        { file_name: fileName, slip_name: slipName, entry_id: entryId }, { headers: getAuthHeader() });
      toast.success(`Linked "${fileName}" → "${slipName}" — future invoices will match it`);
      loadAliases();
      if (r.data.qty_discrepancies) onDiscs(r.data.qty_discrepancies);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the name link"); }
  };

  const unlink = async (id) => {
    try {
      const r = await axios.delete(`${API}/packing-list/name-aliases/${id}`,
        { headers: getAuthHeader(), params: { entry_id: entryId } });
      toast.success("Name link removed");
      loadAliases();
      if (r.data.qty_discrepancies) onDiscs(r.data.qty_discrepancies);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove the link"); }
  };

  return { slipNames, aliases, link, unlink };
};

export const LinkSlipSelect = ({ d, i, slipNames, onLink }) => (
  <select defaultValue="" onChange={(e) => e.target.value && onLink(d.product, e.target.value)}
    className="mt-1 h-6 max-w-[220px] rounded border border-sky-300 bg-sky-50 text-[10px] text-sky-800 px-1"
    data-testid={`qty-mismatch-link-select-${i}`}>
    <option value="">Link to slip name…</option>
    {slipNames.map(n => <option key={n} value={n}>{n}</option>)}
  </select>
);

export const SavedLinksList = ({ aliases, onUnlink }) => {
  if (!aliases.length) return null;
  return (
    <details className="text-[11px] border rounded p-2 bg-slate-50" data-testid="qty-mismatch-saved-links">
      <summary className="cursor-pointer font-semibold text-slate-700">
        <Link2 className="inline w-3 h-3 mr-1 -mt-0.5" />Saved name links ({aliases.length})
      </summary>
      <div className="mt-1 max-h-32 overflow-y-auto space-y-1">
        {aliases.map(a => (
          <div key={a.id} className="flex items-center gap-2" data-testid={`qty-mismatch-link-${a.id}`}>
            <span className="font-medium">{a.file_name}</span>
            <span className="text-slate-400">→</span>
            <span className="text-sky-700">{a.slip_name}</span>
            <span className="text-slate-400 ml-auto">{a.created_by_name || a.created_by}</span>
            <button type="button" onClick={() => onUnlink(a.id)} title="Remove this link"
              className="text-rose-500 hover:text-rose-700" data-testid={`qty-mismatch-unlink-${a.id}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </details>
  );
};
